/**
 * AbilityTestConfig — single visible toggle surface for /abilitytest URL.
 *
 * Sibling to BrawlConfig.ts and PvEConfig.ts. /abilitytest is the
 * canonical single-NPC range-test surface — clean labeled distance
 * map, deterministic far-left spawn, one pig at exactly
 * `<ability range> + pigOffsetFromRangePx` so the cast refuses at
 * spawn and works after a small step right.
 *
 * Wiring:
 *   - main.tsx                    → reads on /abilitytest path mount;
 *                                   injects URL params + reset wipe
 *                                   (mirrors /brawl + /pve)
 *   - AbilityRangeTest.ts         → reads testAbilityId + mobId at
 *                                   module load to compute pig spawn
 *                                   position. Map id = 'ability-range-test'.
 *
 * To test a DIFFERENT ability: change `testAbilityId` here. The pig
 * spawn position recomputes from that ability's `range` field
 * (TOML → abilities.gen.ts). DJ tweaks the range in
 * content/abilities/<class>.toml + npm run compile:content.
 */

export const ABILITY_TEST_CONFIG = {
  // Cookie/cache hygiene — same as /brawl + /pve.
  resetOnLoad: true,

  // Class default — what DJ spawns into. Mage chosen because the
  // first-draft test ability is fireball.
  defaultClass: 'mage' as const,

  // Test ability id. Drives the pig spawn position calculation
  // (pig sits at `playerSpawnX + getAbilityData(testAbilityId).range
  // + pigOffsetFromRangePx`). Change this to test other abilities;
  // the ability must declare a `range` field in TOML.
  testAbilityId: 'fireball' as const,

  // Mob to spawn — must be a key in MOB_CONFIG (entities.gen.ts).
  // `test_pig` is the dedicated /abilitytest variant: same sprite +
  // stats as Wild Pig, but with 100% gold + 100% pig_foot drop rates
  // (content/loot/drop_tables.toml under [test_pig.*]) so every kill
  // produces something lootable for verifying auto-loot vacuum + the
  // pickup-pop VFX. Gold-tinted color (#ffd700) marks the variant.
  mobId: 'test_pig' as const,

  // Pig sits this many px BEYOND the ability's declared `range`.
  // 10 = "1 step closer than out-of-range edge" — DJ has to walk a
  // few pixels right after spawn before the cast clears the range
  // gate. Tweak to test edge cases:
  //   0       = pig exactly at max range (cast should succeed)
  //   negative = pig in range (cast works at spawn)
  //   +50     = clearly out of range (need a bigger walk)
  pigOffsetFromRangePx: 10,

  // HUD overrides — match /brawl + /pve.
  preset: 'live-lobby' as const,
  skipSplash: true,
  skipLogin: true,

  // /abilitytest is the VFX-iteration test surface, so the two debug
  // panels DJ uses for cast verification (TargetAbilityCheckPanel +
  // AbilityLifecycleMonitor) open automatically. Implemented via a
  // `?devPanelsOn=1` URL param injected by main.tsx — UIContext reads
  // the param at init and seeds both panels' visibility to true.
  // Other URLs (/brawl, /pve) leave the panels off-by-default.
  devPanelsOn: true,
} as const;
